import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { cassa } from "./cassaApi";
import { ArticleCard } from "./ArticleCard";
import { FilterChips, emptyFilters } from "./FilterChips";
import { useViewPrefs } from "./colors";

const n = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export function matchedPrinters(printers, q) {
  const words = q.trim().split(/\s+/);
  const full = n(q);
  const noBrand = words.length > 1 ? n(words.slice(1).join("")) : full;
  if (!full) return [];
  return (printers || []).filter((x) => n(x).includes(full) || (noBrand.length > 1 && n(x).includes(noBrand))).slice(0, 4);
}

export function useProductSearch({ q, mode, batch = "", limit = 30, filters = emptyFilters, zeroCost = false }) {
  const [res, setRes] = useState({ total: 0, items: [], brands: [], models: [] });
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const fkey = JSON.stringify(filters) + zeroCost;
  const run = (skip = 0) => {
    const id = ++seq.current;
    setLoading(true);
    const params = { q, mode, batch, skip, limit, brands: filters.brands.join(","), models: filters.models.join(","), color: filters.color, exclude_disc: filters.exclude_disc, zero_cost: zeroCost };
    return cassa.get("/products", { params }).then((r) => {
      if (id !== seq.current) return;
      setRes((p) => (skip ? { ...r.data, items: [...p.items, ...r.data.items] } : r.data));
    }).finally(() => id === seq.current && setLoading(false));
  };
  useEffect(() => { const t = setTimeout(() => run(0), 180); return () => clearTimeout(t); }, [q, mode, batch, fkey]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...res, loading, more: () => run(res.items.length), reload: () => run(0) };
}

export function SearchBox({ q, setQ, mode, setMode, onEnter, autoFocus }) {
  return (
    <div className="flex flex-wrap gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="cassa-search-input" autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
          placeholder={mode === "printer" ? "Modello stampante, es. XP 2200 o Epson XP-2200" : "Codice, descrizione o stampante"}
          className="w-full h-12 pl-10 pr-3 rounded-md border border-slate-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-brand-yellow" />
      </div>
      <div className="inline-flex rounded-md border bg-white p-0.5">
        {[["all", "Tutto"], ["printer", "Per stampante"]].map(([k, l]) => (
          <button key={k} data-testid={`cassa-mode-${k}`} onClick={() => setMode(k)} className={`press h-11 px-3 rounded text-sm font-semibold ${mode === k ? "bg-brand-navy text-white" : ""}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export function ResultList({ s, q, mode, onAdd, filters, setFilters, ptsFor }) {
  const [view, setView] = useViewPrefs();
  return (
    <div className="space-y-2">
      {setFilters && <FilterChips s={s} f={filters} setF={setFilters} view={view} setView={setView} />}
      <div className="rounded-xl border bg-white overflow-hidden" data-testid="cassa-results" style={{ fontSize: `${15 * view.scale}px` }}>
        <div className="px-3 py-2 text-[13px] font-mono text-slate-500 bg-slate-50 border-b flex items-center gap-2">
          {s.loading && <Loader2 className="w-3 h-3 animate-spin" />} {s.total} {s.total === 1 ? "articolo" : "articoli"}{mode === "printer" && q ? " compatibili" : ""}
        </div>
        <div className={`max-h-[64vh] overflow-y-auto p-2 ${view.dense ? "space-y-1" : "space-y-2"}`}>
          {s.items.map((p) => <ArticleCard key={p.id} p={p} hits={q ? matchedPrinters(p.printers, q) : []} onAdd={onAdd} view={view} pts={ptsFor?.(p)} />)}
        </div>
        {!s.items.length && !s.loading && <p className="p-5 text-sm text-slate-500">Nessun articolo trovato.</p>}
        {s.items.length < s.total && <button data-testid="cassa-more-btn" onClick={s.more} className="w-full py-2 text-sm font-semibold text-brand-navy border-t hover:bg-slate-50">Mostra altri</button>}
      </div>
    </div>
  );
}
